import { MaterialData } from './materials-service';
import { calculateBracingCount } from './utils';
import { PricingContext, calculatePrice, ZERO_PRICING } from './pricing';

export interface BOMItem {
  materialId: string;
  description: string;
  descriptionDE: string;
  category: string;
  unit: string;
  quantity: number;
  baseUnitPrice: number;
  unitPrice: number;
  totalPrice: number;
  weight: number;
  totalWeight: number;
  load?: number;
  glbFile: string;
}

export interface BOMSummary {
  items: BOMItem[];
  totalPrice: number;
  totalWeight: number;
  totalLoadCapacity: number;
}

export function calculateBOM(
  materials: MaterialData[],
  config: {
    height: number;
    width: number;
    depth: number;
    shelfCount: number;
    sectionCount: number;
    sides: 'single' | 'double';
    frameState: 'open' | 'closed' | 'outside';
    model: string;
  },
  pricingContext: PricingContext = ZERO_PRICING
): BOMSummary {
  const items: BOMItem[] = [];
  let totalPrice = 0;
  let totalWeight = 0;
  let totalLoadCapacity = 0;

  const heightMM = config.height * 10;
  const widthMM = config.width * 10;
  const depthMM = config.depth * 10;
  const isDoubleSided = config.sides === 'double';

  const priced = (basePrice: number) => calculatePrice(basePrice, pricingContext);

  // Find Frame
  const frameCategory = 'Frame SF';
  const frameType = config.frameState === 'closed' ? 'HDF' : 'Tube';
  const frameMaterial = materials.find(m =>
    m.category === frameCategory &&
    m.length === heightMM &&
    m.width === depthMM &&
    m.description.includes(frameType)
  );

  if (frameMaterial) {
    const baseFrameQuantity = config.sectionCount + 1;
    const frameQuantity = isDoubleSided ? baseFrameQuantity * 2 : baseFrameQuantity;
    const unitPrice = priced(frameMaterial.price);
    items.push({
      materialId: frameMaterial.materialId,
      description: frameMaterial.description,
      descriptionDE: frameMaterial.descriptionDE,
      category: frameMaterial.category,
      unit: frameMaterial.unit,
      quantity: frameQuantity,
      baseUnitPrice: frameMaterial.price,
      unitPrice,
      totalPrice: unitPrice * frameQuantity,
      weight: frameMaterial.weight,
      totalWeight: frameMaterial.weight * frameQuantity,
      glbFile: frameMaterial.glbFile,
    });
    totalPrice += unitPrice * frameQuantity;
    totalWeight += frameMaterial.weight * frameQuantity;
  }

  // Find Shelves
  const shelfCategory = 'Shelf SF';
  const shelfType = config.model === 'L2505' ? 'Type L 2505' : 'Type M 4006';
  const shelfMaterial = materials.find(m =>
    m.category === shelfCategory &&
    m.length === widthMM &&
    m.width === depthMM &&
    m.description.includes(shelfType)
  );

  if (shelfMaterial) {
    const baseShelfQuantity = config.shelfCount * config.sectionCount;
    const shelfQuantity = isDoubleSided ? baseShelfQuantity * 2 : baseShelfQuantity;
    const shelfLoadCapacity = shelfMaterial.load * shelfQuantity;
    const unitPrice = priced(shelfMaterial.price);

    items.push({
      materialId: shelfMaterial.materialId,
      description: shelfMaterial.description,
      descriptionDE: shelfMaterial.descriptionDE,
      category: shelfMaterial.category,
      unit: shelfMaterial.unit,
      quantity: shelfQuantity,
      baseUnitPrice: shelfMaterial.price,
      unitPrice,
      totalPrice: unitPrice * shelfQuantity,
      weight: shelfMaterial.weight,
      totalWeight: shelfMaterial.weight * shelfQuantity,
      load: shelfMaterial.load,
      glbFile: shelfMaterial.glbFile,
    });
    totalPrice += unitPrice * shelfQuantity;
    totalWeight += shelfMaterial.weight * shelfQuantity;
    totalLoadCapacity = shelfLoadCapacity;
  }

 // Find Bracing
  const bracingCategory = 'Bracing SF';
  const bracingType = config.sides === 'double' ? 'Double' : 'Single';
  const bracingWidthMM = config.width === 97 ? 1000 : 1300;

  const bracingMaterial = materials.find(m =>
    m.category === bracingCategory &&
    m.length === heightMM &&
    m.width === bracingWidthMM &&
    m.description.includes(bracingType)
  );

  if (bracingMaterial) {
    // Determine shelf load per shelf from the matched shelf material (kg).
    // shelfMaterial.load is the per-shelf load capacity.
    const shelfLoadPerShelf = shelfMaterial?.load ?? 0;

    // Use the shared bracing calculation (see utils.ts for full rule documentation).
    // Single-sided: 1 per 8 sections.
    // Double-sided: ceiling(max(Rule1, Rule2)), where
    //   Rule 1 = ceil(sections / 8)
    //   Rule 2 = ceil((shelfLoad * sections * shelfLevels * 2) / 24000)
    const bracingQuantity = calculateBracingCount(
      config.sectionCount,
      config.sides,
      shelfLoadPerShelf,
      config.shelfCount
    );

    if (bracingQuantity > 0) {
      items.push({
        materialId: bracingMaterial.materialId,
        description: bracingMaterial.description,
        descriptionDE: bracingMaterial.descriptionDE,
        category: bracingMaterial.category,
        unit: bracingMaterial.unit,
        quantity: bracingQuantity,
        baseUnitPrice: bracingMaterial.price,
        unitPrice: bracingMaterial.price,
        totalPrice: bracingMaterial.price * bracingQuantity,
        weight: bracingMaterial.weight,
        totalWeight: bracingMaterial.weight * bracingQuantity,
        glbFile: bracingMaterial.glbFile,
      });
      totalPrice += bracingMaterial.price * bracingQuantity;
      totalWeight += bracingMaterial.weight * bracingQuantity;
    }
  }

  // Add Dustcaps
  const dustcapMaterial = materials.find(m => m.materialId === '2137');
  if (dustcapMaterial) {
    const baseDustcapQuantity = (config.sectionCount + 1) * 2;
    const dustcapQuantity = isDoubleSided ? baseDustcapQuantity * 2 : baseDustcapQuantity;
    const unitPrice = priced(dustcapMaterial.price);
    items.push({
      materialId: dustcapMaterial.materialId,
      description: dustcapMaterial.description,
      descriptionDE: dustcapMaterial.descriptionDE,
      category: dustcapMaterial.category,
      unit: dustcapMaterial.unit,
      quantity: dustcapQuantity,
      baseUnitPrice: dustcapMaterial.price,
      unitPrice,
      totalPrice: unitPrice * dustcapQuantity,
      weight: dustcapMaterial.weight,
      totalWeight: dustcapMaterial.weight * dustcapQuantity,
      glbFile: dustcapMaterial.glbFile || '',
    });
    totalPrice += unitPrice * dustcapQuantity;
    totalWeight += dustcapMaterial.weight * dustcapQuantity;
  }

  return {
    items,
    totalPrice: Math.round(totalPrice * 100) / 100,
    totalWeight: Math.round(totalWeight * 100) / 100,
    totalLoadCapacity,
  };
}
